import { Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { FirebaseCodeErrorService } from 'src/app/services/firebase-code-error.service';
import { LoadingService } from 'src/app/services/loading.service';

@Component({
    selector: 'app-verify-email',
    templateUrl: './verify-email.component.html',
    styleUrls: ['./verify-email.component.css'],
    standalone: false
})
export class VerifyEmailComponent implements OnDestroy {
  private _authService = inject(AuthService);
  private router = inject(Router);
  private notifier = inject(FirebaseCodeErrorService);
  private _loadingService = inject(LoadingService);

  // Estado (signals)
  error = signal('');
  canResend = signal(true); // Controla si se puede reenviar el email
  countdown = signal(0); // Contador para el temporizador
  loading = computed(() => this._loadingService.isLoading());

  // para mostrar a qué correo se envió
  // si en nativo no hay currentUser, uso localStorage
  email = computed(() => this._authService.currentUser()?.email ?? localStorage.getItem('email') ?? '');

  // tipado del intervalo
  private intervalId: ReturnType<typeof setInterval> | null = null;

  // hidratá sesión al entrar (idempotente, sirve en nativo)
  async ngOnInit(): Promise<void> {
    try {
      await this._authService.ensureSession();
    } catch { /* noop */ }
  }

  async reenviarVerificarEmail() {
    // Evita doble submit si ya está cargando
    if (this.loading() || !this.canResend()) return;

    // Asegurá bootstrap de sesión (web y nativo)
    await this._authService.ensureSession();

    // En web devuelve el idToken del SDK; en nativo, el guardado en Preferences
    const idToken = await this._authService.currentIdToken();
    if (!idToken) {
      this.error.set('Debes iniciar sesión para reenviar el email.');
      return;
    }

    try {
      this.canResend.set(false);
      this.countdown.set(60);
      await this._loadingService.run(async () => {
        await this._authService.verificarEmail(); // usa el método moderno del AuthService
      });
      this.notifier.showSuccess('Verificación enviada. Revisá tu bandeja (y SPAM).', 5000);
      this.startCountdown();
    } catch (err: any) {
      console.error(err); 
      this.error.set('No se pudo enviar el correo de verificación. Intenta nuevamente.');
      this.startCountdown(); // incluso en error, corre el temporizador anti-spam
    }
  }

  // Inicia el temporizador
  private startCountdown() {
    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = setInterval(() => {
      const left = this.countdown() - 1;
      this.countdown.set(left);
      if (left <= 0) {
        if (this.intervalId) clearInterval(this.intervalId);
        this.intervalId = null;
        this.canResend.set(true);
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.intervalId) clearInterval(this.intervalId);
  }

  // (Opcional) si el usuario ya verificó su correo y quiere continuar
  // re-chequeá antes de navegar
  async continuar(): Promise<void> {
    try {
      await this._authService.ensureSession();

      // En web, emailVerified cambia tras reload del user;
      // si tu AuthService expone algo como refresh/reload, ideal llamarlo aquí.
      const u = this._authService.currentUser();
      if (u?.emailVerified) {
        await this.router.navigateByUrl('/order-emergencias');
      } else {
        this.error.set('Aún no detectamos la verificación. Abrí el link del correo y volvé a intentar.');
      }
    } catch (e) {
      console.error(e);
      this.error.set('Hubo un problema al continuar.');
    }
  }


  async volver() {
    // cerrar sesión para poder entrar a /sign-in (guestOnlyMatch)
    await this._authService.logoutFast();
    await this.router.navigateByUrl('/sign-in');
  }
}
